import {
  Component,
  input,
  computed,
  inject,
  ChangeDetectionStrategy,
  signal,
  output,
} from '@angular/core';
import {
  AnalysisTechnicalStatus,
  normalizeTechnicalState,
} from '@shared/models/analysis.model';
import { type PipelineAction } from '@features/analyses/services/analysis-workflow.facade';
import { TimelineStep } from '@shared/models/step-timeline.model';
import { AnalysisTimelineService } from '@shared/services/analysis-timeline.service';
import { ConfirmationDialogService } from '@shared/services/confirmation-dialog.service';
import { StepTimeline } from '@shared/components/step-timeline/step-timeline';
import {
  PIPELINE_STAGE_CONFIGS,
  getStageConfig,
  resolveTaskWindowForStage,
  type PipelineStageConfig,
} from '@features/analyses/config/pipeline-stage.config';
import { environment } from '@env/environment';

const debugLog = (...args: unknown[]) => {
  if (!environment.production) {
    console.log(...args);
  }
};

export interface TimelineActionExecution {
  action: PipelineAction;
  taskNo?: number;
  startTask?: number;
  endTask?: number;
  trigger: 'rerun' | 'start';
}

/**
 * Analysis Timeline Component
 *
 * Displays the SI pipeline workflow as a horizontal step timeline.
 * All stage definitions (task ranges, actions, dialogs) are driven by
 * PIPELINE_STAGE_CONFIGS — add or change a stage there, not here.
 */
@Component({
  selector: 'app-analysis-timeline',
  standalone: true,
  imports: [StepTimeline],
  templateUrl: './analysis-timeline.html',
  styleUrl: './analysis-timeline.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AnalysisTimeline {
  private readonly timelineService = inject(AnalysisTimelineService);
  private readonly confirmationDialog = inject(ConfirmationDialogService);

  timelineMode = input<'standard' | 'technical'>('standard');
  technicalStatus = input<AnalysisTechnicalStatus | null>(null);
  runtime = input<Record<string, unknown> | null>(null);
  showActions = input(true);
  executionWindow = input<{ startTask: number; endTask: number } | null>(null);

  actionExecuted = output<TimelineActionExecution>();
  isProcessing = signal(false);
  hoveredRangeEndTask = signal<number | null>(null);

  private readonly executionStartTask = computed(() => {
    const status = this.technicalStatus();
    const task = Number(status?.task ?? 0);
    const state = normalizeTechnicalState(status?.state);
    if (state === 'completed') return task + 1;
    if (state === 'failed') {
      const stage = PIPELINE_STAGE_CONFIGS.find((config) =>
        (config.tasks as readonly number[]).includes(task),
      );
      return stage?.tasks[0] ?? Math.max(task, 1);
    }
    return Math.max(task, 1);
  });

  // ─── State resolution ───────────────────────────────────────────────────────

  /**
   * Resolves the visual state for a task range using only task number + state.
   *
   * Rules (in priority order):
   *   1. Current task is past this range           → completed
   *   2. Current task hasn't reached this range    → upcoming
   *   3. Current task is within this range:
   *      - errorCode or failed                     → error
   *      - completed and mid-range (< toTask)      → on-hold (more tasks remain)
   *      - completed and at/past range end          → completed
   *      - queued or processing                    → current
   *      - otherwise (unknown / no state yet)      → current
   *
   * No `stage` field is needed — task number alone determines range membership.
   */
  private resolveTaskStateRange(
    fromTask: number,
    toTask: number,
  ): TimelineStep['state'] {
    const status = this.technicalStatus();
    if (!status) return 'upcoming';

    const task = Number(status.task ?? 0);
    const state = normalizeTechnicalState(status.state);
    const hasError = !!(status.errorCode || state === 'failed');

    // Past this range → completed
    if (task > toTask) return 'completed';

    // Not yet reached
    if (task === 0 || task < fromTask) return 'upcoming';

    // Within range
    if (hasError) return 'error';
    if (state === 'completed' && task < toTask) return 'on-hold'; // mid-range pause
    if (state === 'completed') return 'completed';
    if (state === 'queued' || state === 'processing') return 'current';
    if (state === 'draft') return 'on-hold';

    return 'current'; // active task with unknown state
  }

  // ─── Step descriptions ──────────────────────────────────────────────────────

  /**
   * Returns a description string for a stage step.
   * For 'on-hold' mid-range, shows which task completed to distinguish from a
   * user-initiated pause.
   */
  private stepDescription(
    completedText: string,
    state: TimelineStep['state'],
    defaultText: string,
    config: PipelineStageConfig | undefined,
  ): string {
    if (state === 'completed') return completedText;
    if (state === 'error') return 'Fehlgeschlagen';
    if (state === 'on-hold') {
      const currentTask = Number(this.technicalStatus()?.task ?? 0);
      const currentState = normalizeTechnicalState(
        this.technicalStatus()?.state,
      );
      if (
        config &&
        currentState === 'completed' &&
        currentTask >= config.tasks[0] &&
        currentTask < config.tasks[config.tasks.length - 1]
      ) {
        return `Schritt ${currentTask} abgeschlossen`;
      }
      return 'Bereit';
    }
    if (state === 'current') {
      if (normalizeTechnicalState(this.technicalStatus()?.state) === 'queued') {
        return 'Warteschlange';
      }
      const progress = Number(this.technicalStatus()?.progressPercent);
      return Number.isFinite(progress)
        ? `In Bearbeitung · ${Math.round(Math.min(100, Math.max(0, progress)))}%`
        : 'In Bearbeitung';
    }
    return defaultText;
  }

  // ─── Computed steps ─────────────────────────────────────────────────────────

  /**
   * Builds raw TimelineStep[] from PIPELINE_STAGE_CONFIGS.
   * Adding a stage to the config automatically adds it here.
   */
  technicalTimelineSteps = computed<TimelineStep[]>(() => {
    const steps: TimelineStep[] = [
      {
        id: 'created',
        label: 'Erstellt',
        description: 'Analyse angelegt',
        state: 'completed',
      },
    ];

    for (const config of PIPELINE_STAGE_CONFIGS) {
      const first = config.tasks[0];
      const last = config.tasks[config.tasks.length - 1];
      const state = this.resolveTaskStateRange(first, last);
      steps.push({
        id: config.stageId,
        label: config.label,
        description: this.stepDescription(
          `Aufgaben ${first}–${last} abgeschlossen`,
          state,
          `Aufgaben ${first}–${last} ausstehend`,
          config,
        ),
        state: state === 'on-hold' ? 'upcoming' : state,
      });
    }

    return steps;
  });

  /** Decorates steps with action buttons derived from config. */
  timelineSteps = computed<TimelineStep[]>(() => {
    const baseSteps = this.technicalTimelineSteps();
    const pendingWindow = this.executionWindow();
    const rangeStart = pendingWindow?.startTask ?? this.executionStartTask();
    const rangeEnd = pendingWindow?.endTask ?? this.hoveredRangeEndTask();

    return baseSteps.map((step) => {
      const config = getStageConfig(String(step.id));
      if (!config) return step;
      const stageStart = config.tasks[0];
      const stageEnd = config.tasks[config.tasks.length - 1];
      const isInExecutionRange =
        rangeEnd !== null && stageEnd >= rangeStart && stageStart <= rangeEnd;
      const rangeData = isInExecutionRange
        ? { ...step.data, executionRange: true, executionPending: !!pendingWindow }
        : step.data;

      const isActive =
        step.state === 'current' && this.isTechnicalExecutionActive();

      if (isActive) {
        return {
          ...step,
          data: rangeData,
          actionLabel: 'Stoppen',
          actionDisabled: this.isProcessing(),
          actionTitle: 'Laufende Ausführung stoppen',
          nodeActionIcon: 'lucideSquare',
          nodeActionTitle: 'Ausführung stoppen',
          nodeActionDisabled: this.isProcessing(),
        };
      }

      if (step.state === 'completed' || step.state === 'error') {
        return {
          ...step,
          data: rangeData,
          actionLabel: 'Wiederholen',
          actionDisabled: this.isProcessing(),
          actionTitle: 'Step erneut ausführen',
          nodeActionIcon: 'lucideRotateCcw',
          nodeActionTitle: 'Step wiederholen',
          nodeActionDisabled: this.isProcessing(),
        };
      }

      if (step.state === 'on-hold') {
        return {
          ...step,
          data: rangeData,
          actionLabel: 'Weiter',
          actionDisabled: this.isProcessing(),
          actionTitle: 'Berechnung fortsetzen',
          nodeActionIcon: 'lucidePlay',
          nodeActionTitle: 'Berechnung fortsetzen',
          nodeActionDisabled: this.isProcessing(),
        };
      }

      if (!this.isTechnicalExecutionActive() && stageEnd >= this.executionStartTask()) {
        return {
          ...step,
          data: rangeData,
          actionLabel: 'Bis hier ausführen',
          actionDisabled: this.isProcessing(),
          actionTitle: `Aufgaben ${this.executionStartTask()}–${stageEnd} ausführen`,
          nodeActionIcon: 'lucidePlay',
          nodeActionTitle: `Bis ${step.label} ausführen`,
          nodeActionDisabled: this.isProcessing(),
        };
      }

      return { ...step, data: rangeData };
    });
  });

  // ─── Action map ─────────────────────────────────────────────────────────────

  private readonly stepActionMap = computed<
    Record<string, { action: PipelineAction }>
  >(() => {
    const baseSteps = this.technicalTimelineSteps();
    const map: Record<
      string,
      { action: PipelineAction }
    > = {};

    baseSteps.forEach((step) => {
      const config = getStageConfig(String(step.id));
      if (!config) return;

      const stageEnd = config.tasks[config.tasks.length - 1];
      const isActionable =
        (step.state === 'current' && this.isTechnicalExecutionActive()) ||
        step.state === 'completed' ||
        step.state === 'error' ||
        step.state === 'on-hold' ||
        (!this.isTechnicalExecutionActive() && stageEnd >= this.executionStartTask());

      if (isActionable) {
        const isCancel =
          step.state === 'current' && this.isTechnicalExecutionActive();
        map[String(step.id)] = {
          action: isCancel
            ? {
                action: 'cancel-analysis',
                label: 'Ausführung stoppen',
                description: 'Laufende technische Ausführung stoppen',
                hasPermission: true,
                requiredPermission: 'analysis:start_analysis',
                disabled: false,
              }
            : this.createActionForStage(config),
        };
      }
    });

    return map;
  });

  /** Builds a minimal PipelineAction for a stage from its config. */
  private createActionForStage(config: PipelineStageConfig): PipelineAction {
    if (config.action === 'start-planning') {
      return {
        action: 'start-planning',
        targetStatus: 'planning',
        label: 'Planung starten',
        description: 'Planungs-Task starten',
        hasPermission: true,
        requiredPermission: 'analysis:start_planning',
        disabled: false,
      };
    }
    return {
      action: 'start-analysis',
      targetStatus: 'analyzing',
      label: 'Analyse starten',
      description: 'Technische Analyse starten',
      hasPermission: true,
      requiredPermission: 'analysis:start_analysis',
      disabled: false,
    };
  }

  // ─── Step helpers ────────────────────────────────────────────────────────────

  private isTechnicalExecutionActive(): boolean {
    const state = normalizeTechnicalState(this.technicalStatus()?.state);
    return state === 'queued' || state === 'processing';
  }

  /** Returns the best task number to report for a step (current task if within range, else first). */
  private resolveTaskNoForStep(stepId: unknown): number | undefined {
    const config = getStageConfig(String(stepId || ''));
    if (!config) return undefined;
    const currentTask = Number(this.technicalStatus()?.task ?? 0);
    const first = config.tasks[0];
    const last = config.tasks[config.tasks.length - 1];
    return currentTask >= first && currentTask <= last ? currentTask : first;
  }

  /** Derives the task window for executing a step. Uses PIPELINE_STAGE_CONFIGS. */
  private resolveTaskRangeForStep(
    stepId: unknown,
    trigger: 'rerun' | 'start',
  ): { startTask: number; endTask: number } | undefined {
    const config = getStageConfig(String(stepId || ''));
    if (!config) return undefined;
    const currentTask = Number(this.technicalStatus()?.task ?? 0);
    return resolveTaskWindowForStage(config, trigger, currentTask);
  }

  // ─── Public action handlers ──────────────────────────────────────────────────

  onActionExecute(
    action: PipelineAction,
    context?: {
      taskNo?: number;
      startTask?: number;
      endTask?: number;
      trigger?: 'rerun' | 'start';
    },
  ): void {
    if (!action.hasPermission) {
      debugLog('[AnalysisTimeline] Action not permitted:', action.action);
      return;
    }

    this.isProcessing.set(true);
    this.actionExecuted.emit({
      action,
      taskNo: context?.taskNo,
      startTask: context?.startTask,
      endTask: context?.endTask,
      trigger: context?.trigger || 'start',
    });

    setTimeout(() => this.isProcessing.set(false), 500);
  }

  onStepActionExecute(step: TimelineStep): void {
    const entry = this.stepActionMap()[String(step.id)];
    if (!entry) return;

    const trigger: 'rerun' | 'start' =
      step.state === 'completed' || step.state === 'error' ? 'rerun' : 'start';
    const taskNo = this.resolveTaskNoForStep(step.id);
    const range = this.resolveTaskRangeForStep(step.id, trigger);

    this.onActionExecute(entry.action, {
      taskNo,
      startTask: range?.startTask,
      endTask: range?.endTask,
      trigger,
    });
  }

  async onNodeActionExecute(step: TimelineStep): Promise<void> {
    const entry = this.stepActionMap()[String(step.id)];
    if (!entry) return;

    if (entry.action.action.replaceAll('_', '-') === 'cancel-analysis') {
      const confirmed = await this.confirmationDialog.confirm({
        title: 'Ausführung stoppen?',
        description: `Die laufende Ausführung bei „${step.label}“ wird abgebrochen.`,
        type: 'warning',
        confirmText: 'Ausführung stoppen',
      });
      if (!confirmed) return;
    }

    const config = getStageConfig(String(step.id));
    const startTask = this.executionStartTask();
    const selectedEndTask = config?.tasks[config.tasks.length - 1];
    if (
      config &&
      step.state !== 'completed' &&
      step.state !== 'error' &&
      selectedEndTask !== undefined &&
      selectedEndTask >= startTask
    ) {
      this.hoveredRangeEndTask.set(null);
      this.onActionExecute(entry.action, {
        taskNo: startTask,
        startTask,
        endTask: selectedEndTask,
        trigger: 'start',
      });
      return;
    }

    const trigger: 'rerun' | 'start' =
      step.state === 'completed' || step.state === 'error' ? 'rerun' : 'start';
    const taskNo = this.resolveTaskNoForStep(step.id);
    const range = this.resolveTaskRangeForStep(step.id, trigger);

    this.onActionExecute(entry.action, {
      taskNo,
      startTask: range?.startTask,
      endTask: range?.endTask,
      trigger,
    });
  }

  onStepHover(step: TimelineStep | null): void {
    if (!step || this.isTechnicalExecutionActive()) {
      this.hoveredRangeEndTask.set(null);
      return;
    }
    const config = getStageConfig(String(step.id));
    const endTask = config?.tasks[config.tasks.length - 1];
    this.hoveredRangeEndTask.set(
      endTask !== undefined && endTask >= this.executionStartTask() ? endTask : null,
    );
  }

  // ─── Misc helpers ────────────────────────────────────────────────────────────

  formatDate(date: Date | undefined): string {
    return this.timelineService.formatDate(date);
  }

  formatTimestamp(date: Date | string | undefined): string {
    if (!date) return '';
    try {
      const d = typeof date === 'string' ? new Date(date) : date;
      return d.toLocaleString('de-DE', {
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return '';
    }
  }

  getActionIcon(actionName: string): string {
    const iconMap: Record<string, string> = {
      'start-analysis': 'lucidePlay',
      'view-report': 'lucideFileText',
      'send-analysis': 'lucideSend',
      'contact-customer': 'lucidePhone',
      'mark-accepted': 'lucideCircleCheck',
      'archive-analysis': 'lucideArchive',
      'reject-analysis': 'lucideX',
      'reset-analysis': 'lucideRotateCcw',
      'refresh-analysis': 'lucideRefreshCw',
    };
    return iconMap[actionName] || 'lucidePlay';
  }

  getActionVariant(actionName: string): 'default' | 'destructive' | 'outline' {
    if (actionName === 'reject-analysis' || actionName === 'archive-analysis')
      return 'destructive';
    if (actionName.includes('reset') || actionName.includes('refresh'))
      return 'outline';
    return 'default';
  }
}
